/**
 * CompanyDetail
 *
 * Full detail page for a YallyHub vennootschap.
 * Three tabs: Gegevens | Juridisch & Fiscaal | Documenten
 *
 * Merges:
 * - Detail page structure + Sentry + documents tab (bjorn)
 * - Legal tab + shareholders/directors/employees (jonas-test-2)
 */

import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSession } from "@clerk/clerk-react";
import { Tabs, Tab } from "@heroui/react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import {
  Briefcase,
  FileText,
  Scales,
  FloppyDisk,
  ArrowLeft,
  Info,
} from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { useHasPermission } from "hooks/usePermissions";
import CompanyGegevensTab from "../components/CompanyGegevensTab";
import CompanyLegalTab from "../components/CompanyLegalTab";
import CompanyDocumentsTab from "../components/CompanyDocumentsTab";

const VALID_TABS = new Set(["details", "legal", "documents"]);

const resolveTabFromParams = (params: URLSearchParams) => {
  const t = params.get("tab") || "";
  return VALID_TABS.has(t) ? t : "details";
};

const CompanyDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { session } = useSession();

  const canAccessCorporate = useHasPermission("canAccessCorporate");
  const hasEditRights = canAccessCorporate;

  const [company, setCompany] = useState<any>(null);
  const [editedCompany, setEditedCompany] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>(() =>
    resolveTabFromParams(searchParams),
  );

  useEffect(() => {
    const next = resolveTabFromParams(searchParams);
    setSelectedTab((prev) => (prev === next ? prev : next));
  }, [searchParams]);

  const handleTabChange = (key: string) => {
    setSelectedTab(key);
    const next = new URLSearchParams(searchParams);
    if (key === "details") next.delete("tab");
    else next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (id) {
      fetchCompany();
    }
  }, [id]);

  const fetchCompany = async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/companies/${id}/detail`,
        { method: "GET" },
        session,
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch company: ${response.status}`);
      }
      const data = await response.json();
      setCompany(data);
      setEditedCompany(data);
      setHasChanges(false);
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: "CompanyDetail.fetchCompany", companyId: id },
      });
      console.error("Error fetching company:", error);
      toast.error("Fout bij ophalen van vennootschap");
    }
  };

  const handleFieldChange = (field: string, value: any) => {
    setEditedCompany((prev: any) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!company?.id) return;
    setIsSaving(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/companies/${company.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(editedCompany),
        },
        session,
      );

      if (!response.ok) {
        throw new Error("Failed to update company");
      }

      const updatedCompany = await response.json();
      setCompany(updatedCompany);
      setEditedCompany(updatedCompany);
      setHasChanges(false);
      toast.success("Vennootschap succesvol bijgewerkt");
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: "CompanyDetail.handleSaveChanges", companyId: company?.id },
      });
      console.error("Error updating company:", error);
      toast.error("Fout bij bijwerken van vennootschap");
    } finally {
      setIsSaving(false);
    }
  };

  if (!company) {
    return <div />;
  }

  return (
    <div className="z-10 mb-10 flex flex-col rounded-lg bg-white p-4 shadow-md">
      <header className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-moss-primary/10 p-2">
            <Briefcase className="h-5 w-5 text-moss-primary" weight="duotone" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-neutral-primary">
              {company.name}
            </h2>
            <p className="text-sm text-neutral-secondary-mid">
              Vennootschapsdetails en documenten
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!hasEditRights && (
            <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5">
              <Info className="h-4 w-4 text-blue-600" weight="duotone" />
              <span className="text-xs font-medium text-blue-900">
                Alleen-lezen modus
              </span>
            </div>
          )}
          {hasChanges && hasEditRights && (
            <ButtonNew
              variant="primaryMoss"
              onClick={handleSaveChanges}
              icon={<FloppyDisk weight="duotone" />}
              label="Opslaan"
              isDisabled={isSaving}
            />
          )}
          <ButtonNew
            variant="secondaryNeutralMid"
            onClick={() => navigate(-1)}
            icon={<ArrowLeft weight="bold" />}
            label="Terug"
          />
        </div>
      </header>

      <Tabs
        selectedKey={selectedTab}
        onSelectionChange={(key) => handleTabChange(key as string)}
        variant="underlined"
        classNames={{
          tabList:
            "gap-6 w-full relative rounded-none p-0 border-b border-divider",
          cursor: "w-full bg-moss-primary",
          tab: "max-w-fit px-0 h-12",
          tabContent: "group-data-[selected=true]:text-moss-primary",
        }}
      >
        <Tab
          key="details"
          title={
            <div className="flex items-center gap-2">
              <Briefcase className="h-4 w-4" weight="duotone" />
              <span>Gegevens</span>
            </div>
          }
        >
          <CompanyGegevensTab
            company={editedCompany}
            onChange={handleFieldChange}
            isDisabled={!hasEditRights}
          />
        </Tab>

        <Tab
          key="legal"
          title={
            <div className="flex items-center gap-2">
              <Scales className="h-4 w-4" weight="duotone" />
              <span>Juridisch & Fiscaal</span>
            </div>
          }
        >
          <CompanyLegalTab
            company={editedCompany}
            onChange={handleFieldChange}
            isDisabled={!hasEditRights}
          />
        </Tab>

        <Tab
          key="documents"
          title={
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4" weight="duotone" />
              <span>Documenten</span>
            </div>
          }
        >
          <div className="py-6">
            <CompanyDocumentsTab
              company={company}
              refreshCompany={fetchCompany}
              hasEditRights={hasEditRights}
            />
          </div>
        </Tab>
      </Tabs>
    </div>
  );
};

export default CompanyDetail;
